import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import {
  acquireDocumentScrollLock,
  debounce,
  isClickedOutside,
  onAnimationEnd,
  releaseDocumentScrollLock,
} from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  #state = 'closed';
  #operationId = 0;
  #openFrame = 0;
  #closePromise = null;
  #componentClosing = false;

  connectedCallback() {
    super.connectedCallback();

    this.refs.dialog.addEventListener('cancel', this.#handleCancel);
    this.refs.dialog.addEventListener('close', this.#handleNativeClose);

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#operationId += 1;
    if (this.#openFrame) cancelAnimationFrame(this.#openFrame);
    this.#openFrame = 0;

    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
      this.#handleResize.cancel();
    }

    const { dialog } = this.refs;
    const wasActive = this.#state !== 'closed' || dialog.open;
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    dialog.removeEventListener('cancel', this.#handleCancel);
    dialog.removeEventListener('close', this.#handleNativeClose);

    this.#componentClosing = true;
    if (dialog.open) dialog.close();
    this.#componentClosing = false;
    dialog.classList.remove('dialog-closing');
    dialog.style.animation = '';
    if (wasActive) removeTrapFocus();
    releaseDocumentScrollLock(dialog);
    this.#state = 'closed';
    this.#closePromise = null;
    this.#previouslyFocusedElement = null;

    if (wasActive) this.dispatchEvent(new DialogCloseEvent());
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /** @type {HTMLElement | null} */
  #previouslyFocusedElement = null;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open || this.#state !== 'closed') return;

    this.#state = 'opening';
    const operationId = ++this.#operationId;
    this.#previouslyFocusedElement =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    this.#openFrame = requestAnimationFrame(() => {
      this.#openFrame = 0;
      if (operationId !== this.#operationId || this.#state !== 'opening' || !this.isConnected) return;

      acquireDocumentScrollLock(dialog);
      try {
        dialog.showModal();
      } catch (error) {
        releaseDocumentScrollLock(dialog);
        this.#state = 'closed';
        this.#previouslyFocusedElement = null;
        return;
      }

      this.#state = 'open';
      trapFocus(dialog);
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = () => {
    const { dialog } = this.refs;

    if (this.#state === 'opening') {
      this.#operationId += 1;
      if (this.#openFrame) cancelAnimationFrame(this.#openFrame);
      this.#openFrame = 0;
      this.#state = 'closed';
      releaseDocumentScrollLock(dialog);
      this.#previouslyFocusedElement = null;
      return Promise.resolve();
    }

    if (this.#state === 'closing') return this.#closePromise ?? Promise.resolve();
    if (!dialog.open && this.#state === 'closed') return Promise.resolve();

    this.#state = 'closing';
    const operationId = ++this.#operationId;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    this.#closePromise = onAnimationEnd(dialog, undefined, { subtree: false })
      .then(() => {
        if (operationId !== this.#operationId || this.#state !== 'closing') return;
        this.#finishClose({ restoreFocus: true, dispatchEvent: true });
      })
      .finally(() => {
        if (operationId === this.#operationId) this.#closePromise = null;
      });

    return this.#closePromise;
  };

  /**
   * Finalize a dialog close exactly once.
   * @param {{ restoreFocus?: boolean, dispatchEvent?: boolean }} options
   */
  #finishClose({ restoreFocus = false, dispatchEvent = false } = {}) {
    const { dialog } = this.refs;
    const previouslyFocusedElement = this.#previouslyFocusedElement;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.#componentClosing = true;
    if (dialog.open) dialog.close();
    this.#componentClosing = false;
    dialog.classList.remove('dialog-closing');
    dialog.style.animation = '';
    removeTrapFocus();
    releaseDocumentScrollLock(dialog);
    this.#state = 'closed';
    this.#previouslyFocusedElement = null;

    if (restoreFocus && previouslyFocusedElement?.getClientRects().length) {
      previouslyFocusedElement.focus({ preventScroll: true });
    }
    if (dispatchEvent) this.dispatchEvent(new DialogCloseEvent());
  }

  #handleCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  #handleNativeClose = () => {
    if (this.#componentClosing || this.#state === 'closed') return;
    this.#operationId += 1;
    this.#finishClose({ restoreFocus: true, dispatchEvent: true });
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        if (event.target.closest('header-drawer')) return;
        if (event.target.open) {
          acquireDocumentScrollLock(event.target);
        } else {
          releaseDocumentScrollLock(event.target);
        }
      }
    }
  },
  { capture: true }
);

